/**
 * use-enable-notifications.ts — Bật "Thông báo trình duyệt" bằng MỘT chạm: xin quyền của trình duyệt (nếu chưa hỏi), bật tuỳ chọn
 * `desktop` trong Cài đặt cá nhân (không thì có quyền mà vẫn không thông báo gì) và đăng ký nhận thông báo đẩy khi đóng ứng dụng.
 * Dùng chung cho hộp xin quyền lúc vào web và nút "Bật thông báo" trong khung chuông.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { enablePush } from '@/lib/web-push'
import { useSaveMyNotifyPrefs } from '@/hooks/use-notification-settings'
import { desktopPermission } from '@/components/shared/notification-center'
import type { DesktopPermission } from '@/lib/notify/notify-decision'

/** enabled = đã bật xong · denied = trình duyệt đang chặn · dismissed = đóng hộp của trình duyệt mà không chọn · unsupported = không hỗ trợ. */
export type EnableOutcome = 'enabled' | 'denied' | 'dismissed' | 'unsupported'

export type BellBanner = 'hidden' | 'enable' | 'denied'

/**
 * Dải gợi ý trong khung chuông: chưa bật mà còn bật được → nút "Bật"; trình duyệt đang chặn → chỉ chỉ đường mở lại;
 * đã bật / không hỗ trợ / đang xem dưới quyền (không đổi được tuỳ chọn) → ẩn.
 */
export function bellBanner(perm: DesktopPermission, desktopPref: boolean, readOnly = false): BellBanner {
  if (readOnly || perm === 'unsupported') return 'hidden'
  if (perm === 'denied') return 'denied'
  if (perm === 'granted' && desktopPref) return 'hidden'
  return 'enable' // 'default', hoặc đã cấp quyền nhưng tuỳ chọn đang tắt
}

export function useEnableNotifications() {
  const save = useSaveMyNotifyPrefs()
  const [busy, setBusy] = useState(false)

  const enable = async (): Promise<EnableOutcome> => {
    setBusy(true)
    let p: DesktopPermission = desktopPermission()
    try {
      if (p === 'default') p = (await Notification.requestPermission()) as DesktopPermission
    } catch {
      p = desktopPermission()
    }
    setBusy(false)
    if (p === 'unsupported') return 'unsupported'
    if (p === 'granted') {
      save.mutate({ desktop: true }, { onError: (e) => toast.error(apiError(e)) })
      void enablePush() // cả khi đóng ứng dụng (nếu thiết bị + máy chủ hỗ trợ); không được thì thông báo khi đang mở vẫn chạy
      toast.success(i18n.t('sharedMisc:enableNotifications.enabled'))
      return 'enabled'
    }
    if (p === 'denied') {
      // Trình duyệt nhớ lựa chọn → không hỏi lại được; chỉ chỉ đường mở lại.
      toast.error(i18n.t('sharedMisc:enableNotifications.denied'))
      return 'denied'
    }
    return 'dismissed'
  }

  return { enable, busy }
}
